import React from "react";
import { CustomerOrder } from "src/interfaces/order.interface";
import {
  buildEnterprisePackageSlipData,
  chunkOrders,
} from "src/features/ActiveOrders/utils/enterprisePackageSlip";

interface EnterprisePackageSlipPrintViewProps {
  orders: CustomerOrder[];
}

const SLIPS_PER_PAGE = 8;
const EMPTY_GROUP = "Unassigned";

const EnterprisePackageSlipPrintView = React.forwardRef<HTMLDivElement, EnterprisePackageSlipPrintViewProps>(
  ({ orders }, ref) => {
    const pages = buildGroupedPrintPages(orders);

    return (
      <div ref={ref} className="enterprise-package-slip-print-root">
        <style>
          {`
            @media screen {
              .enterprise-package-slip-print-root {
                display: none;
              }
            }

            @media print {
              @page {
                size: A4 portrait;
                margin: 8mm;
              }

              html,
              body {
                background: #fff !important;
              }

              .enterprise-package-slip-print-root {
                display: block;
                color: #111827;
                font-family: Inter, Arial, sans-serif;
              }

              .enterprise-slip-sheet {
                box-sizing: border-box;
                width: 194mm;
                min-height: 281mm;
                display: grid;
                grid-template-columns: repeat(2, minmax(0, 1fr));
                grid-template-rows: repeat(4, 1fr);
                gap: 4mm;
                break-after: page;
                page-break-after: always;
              }

              .enterprise-slip-sheet:last-child {
                break-after: auto;
                page-break-after: auto;
              }

              .enterprise-slip-cell {
                box-sizing: border-box;
                min-width: 0;
                min-height: 0;
                overflow: hidden;
                border: 1px solid #111827;
                border-radius: 2mm;
                padding: 3.5mm;
                display: flex;
                flex-direction: column;
                gap: 1.5mm;
                break-inside: avoid;
                page-break-inside: avoid;
              }

              .enterprise-slip-cell-empty {
                border-color: transparent;
              }

              .enterprise-slip-field {
                display: grid;
                grid-template-columns: 25mm minmax(0, 1fr);
                gap: 2mm;
                font-size: 8.5pt;
                line-height: 1.22;
              }

              .enterprise-slip-label {
                color: #4b5563;
                font-weight: 700;
              }

              .enterprise-slip-value {
                min-width: 0;
                font-weight: 700;
                overflow-wrap: anywhere;
              }

              .enterprise-slip-address {
                display: flex;
                flex-direction: column;
                gap: 0.5mm;
              }

              .enterprise-slip-order-id {
                font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
                font-size: 6.8pt;
                line-height: 1.15;
              }

              .enterprise-slip-footer {
                margin-top: auto;
                border-top: 1px solid #d1d5db;
                padding-top: 1.5mm;
                font-size: 8pt;
                line-height: 1.1;
                font-weight: 800;
                text-transform: uppercase;
              }
            }
          `}
        </style>

        {pages.map((pageOrders, pageIndex) => (
          <section className="enterprise-slip-sheet" key={pageIndex}>
            {pageOrders.map((order) => (
              <EnterprisePackageSlipCell key={order.correlationId} order={order} />
            ))}
            {Array.from({ length: SLIPS_PER_PAGE - pageOrders.length }).map((_, index) => (
              <div
                className="enterprise-slip-cell enterprise-slip-cell-empty"
                key={`empty-${pageIndex}-${index}`}
                aria-hidden="true"
              />
            ))}
          </section>
        ))}
      </div>
    );
  }
);

EnterprisePackageSlipPrintView.displayName = "EnterprisePackageSlipPrintView";

function buildGroupedPrintPages(orders: CustomerOrder[]): CustomerOrder[][] {
  const groups = new Map<string, CustomerOrder[]>();
  orders.forEach((order) => {
    const slip = buildEnterprisePackageSlipData(order);
    const groupKey = normalizeSortText(slip.companySite) || EMPTY_GROUP;
    const groupOrders = groups.get(groupKey) ?? [];
    groupOrders.push(order);
    groups.set(groupKey, groupOrders);
  });

  return Array.from(groups.entries())
    .sort(([a], [b]) => a.localeCompare(b, undefined, { sensitivity: "base" }))
    .flatMap(([, groupOrders]) => {
      const sortedGroupOrders = [...groupOrders].sort((a, b) => {
        const aSlip = buildEnterprisePackageSlipData(a);
        const bSlip = buildEnterprisePackageSlipData(b);
        const customerCompare = normalizeSortText(aSlip.customer).localeCompare(
          normalizeSortText(bSlip.customer),
          undefined,
          { sensitivity: "base" }
        );
        if (customerCompare !== 0) return customerCompare;
        return aSlip.orderNumber.localeCompare(bSlip.orderNumber, undefined, {
          numeric: true,
          sensitivity: "base",
        });
      });
      return chunkOrders(sortedGroupOrders, SLIPS_PER_PAGE);
    });
}

function normalizeSortText(value: string | undefined): string {
  return (value ?? "").trim().toLowerCase();
}

function EnterprisePackageSlipCell({ order }: { order: CustomerOrder }) {
  const slip = buildEnterprisePackageSlipData(order);
  return (
    <article className="enterprise-slip-cell">
      <SlipField label="Customer" value={slip.customer} />
      {slip.companySite ? <SlipField label="Company / Site" value={slip.companySite} /> : null}
      {slip.pickupLocation ? <SlipField label="Pickup location" value={slip.pickupLocation} /> : null}
      {slip.addressLines.length > 0 ? (
        <SlipField
          label="Address"
          value={
            <span className="enterprise-slip-address">
              {slip.addressLines.map((line, index) => (
                <span key={index}>{line}</span>
              ))}
            </span>
          }
        />
      ) : null}
      <SlipField label="Order number" value={slip.orderNumber} />
      {slip.deliveryDate ? <SlipField label="Delivery date" value={slip.deliveryDate} /> : null}
      <SlipField label="Order ID" value={<span className="enterprise-slip-order-id">{slip.orderId}</span>} />
      <div className="enterprise-slip-footer">For delivery package</div>
    </article>
  );
}

function SlipField({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="enterprise-slip-field">
      <span className="enterprise-slip-label">{label}</span>
      <span className="enterprise-slip-value">{value}</span>
    </div>
  );
}

export default EnterprisePackageSlipPrintView;
